import { LeftRight } from "@/components/LeftRight.tsx";
import { WSelect, type ConfiguredWSelectProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { ResourceIcon } from "@/components/icon/ResourceIcon.tsx";
import { useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { getDefaultVpc } from "@/modules/network/networkLogic.ts";
import { getSingleQuery, type VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

export interface VpcNetworkSelectProps extends ConfiguredWSelectProps<VpcSelectItem, string> {
    location: string;
}

export interface VpcSelectItem extends Pick<VpcWithType, "$type" | "name" | "subnet" | "location" | "is_default"> {
    uuid: VpcWithType["uuid"] | null;
}

function ShortInfo({ item }: { item: VpcSelectItem }): ReactNode {
    return item.subnet;
}

const NEW_NETWORK_KEY = "__new_network__";

function VpcNetworkSelectCustom({ items, ...props }: VpcNetworkSelectProps) {
    const { t } = useLingui();
    if (items.length === 0) {
        // When there are no VPCs, provide a dummy "New network" option
        const dummyNewNetwork: VpcSelectItem = {
            $type: "vpc",
            is_default: true,
            location: props.location,
            name: t`New network`,
            subnet: "",
            uuid: null,
        };
        items = [dummyNewNetwork];
    }

    return (
        <WSelect
            data-testid="VpcNetworkSelect"
            dynamicDefault={getDefaultVpc}
            getKey={(item) => item.uuid ?? NEW_NETWORK_KEY}
            getTextValue={(item) => item.name ?? ""}
            searchItems
            items={items}
            {...props}
        >
            {(item) => (
                <LeftRight icon={<ResourceIcon item={item} />} title={item.name} right={<ShortInfo item={item} />} />
            )}
        </WSelect>
    );
}

export type VpcNetworkSelectLoaderProps = Omit<VpcNetworkSelectProps, "items">;

function VpcNetworkSelectLoader(props: VpcNetworkSelectLoaderProps) {
    const { location } = props;
    const { data } = useStandardSuspenseQuery(getSingleQuery, { location });
    return <VpcNetworkSelectCustom items={[...data.values()]} {...props} />;
}

export function VpcNetworkSelect(props: VpcNetworkSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <VpcNetworkSelectCustom {...props} {...renderProps} />}>
            <VpcNetworkSelectLoader {...props} />
        </WSelectWrapper>
    );
}
